import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const DEFAULT_MAX_PRODUCTS = 4;

class RecentlyViewedProducts extends Component {
  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Whether the recently viewed products have already been loaded
   * @type {boolean}
   */
  #loaded = false;

  /**
   * IntersectionObserver to lazy-load the section when near the viewport
   * @type {IntersectionObserver}
   */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;

        observer.disconnect();
        this.#loadRecentlyViewed();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Reads product IDs from localStorage, fetches section HTML via the
   * Section Rendering API with a search query, morphs the section, then
   * reorders the rendered cards to match localStorage order.
   */
  async #loadRecentlyViewed() {
    if (this.#loaded) return;

    const { sectionId, searchUrl, excludeProductId, maxProducts } = this.dataset;

    if (!sectionId || !searchUrl) {
      throw new Error('recently-viewed-products: data-section-id and data-search-url are required');
    }

    let ids = /** @type {string[]} */ (JSON.parse(localStorage.getItem('viewedProducts') || '[]'));

    if (excludeProductId) {
      ids = ids.filter((id) => id !== excludeProductId);
    }

    const limit = parseInt(maxProducts ?? String(DEFAULT_MAX_PRODUCTS), 10);
    ids = ids.slice(0, limit);

    if (ids.length === 0) {
      this.classList.add('hidden');
      return;
    }

    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=${sectionId}`;

    try {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        this.#handleError(new Error(`Server returned ${response.status}`));
        return;
      }

      const html = await response.text();

      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      this.#reorderCards(ids);
      this.#loaded = true;
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.#handleError(/** @type {Error} */ (error));
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Reorders `.resource-list__item` elements within the grid to match the
   * order of product IDs stored in localStorage.
   * @param {string[]} orderedIds - Product IDs in most-recently-viewed order
   */
  #reorderCards(orderedIds) {
    const lists = this.querySelectorAll('.resource-list');

    for (const list of lists) {
      /** @type {Map<string, Element>} */
      const itemByProductId = new Map();

      for (const item of list.querySelectorAll('.resource-list__item')) {
        const card = item.querySelector('product-card[data-product-id]');
        if (card) {
          const productId = /** @type {HTMLElement} */ (card).dataset.productId;
          if (productId) itemByProductId.set(productId, item);
        }
      }

      for (const id of orderedIds) {
        const item = itemByProductId.get(id);
        if (item) list.append(item);
      }
    }
  }

  /**
   * Logs the error and hides the section
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
